import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";

export default function Progress() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ exercise_name: "Back Squat", value: "", unit: "lbs", reps: "", date: "" });
  const [selectedExercise, setSelectedExercise] = useState(null);

  const fetchEntries = () => {
    setLoading(true);
    api
      .get("/progress/me")
      .then(({ data }) => {
        setEntries(data);
        const names = [...new Set(data.map((e) => e.exercise_name))];
        if (names.length) setSelectedExercise((prev) => prev || names[0]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const exerciseNames = useMemo(() => [...new Set(entries.map((e) => e.exercise_name))], [entries]);
  const chartData = useMemo(
    () => entries.filter((e) => e.exercise_name === selectedExercise).map((e) => ({ date: e.date, value: e.value })),
    [entries, selectedExercise]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.exercise_name || !form.value) return;
    setSubmitting(true);
    try {
      await api.post("/progress/me", {
        ...form,
        value: parseFloat(form.value),
        reps: form.reps ? parseInt(form.reps) : undefined,
      });
      toast.success("PR logged!");
      setForm({ exercise_name: form.exercise_name, value: "", unit: "lbs", reps: "", date: "" });
      fetchEntries();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/progress/me/${id}`);
      fetchEntries();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-progress-page">
      <h1 className="font-anton uppercase tracking-tighter text-cream text-3xl sm:text-4xl mb-8">Progress & PRs</h1>

      <form onSubmit={handleSubmit} className="bg-surface border border-line p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-5 gap-4 items-end mb-10" data-testid="progress-form">
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-widest text-smoke">Exercise</label>
          <Input
            value={form.exercise_name}
            onChange={(e) => setForm({ ...form, exercise_name: e.target.value })}
            placeholder="e.g. Back Squat"
            className="rounded-none bg-void border-line text-cream mt-2 h-11 focus-visible:ring-gold focus-visible:border-gold"
            data-testid="progress-exercise-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-smoke">Value</label>
          <Input
            type="number"
            required
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className="rounded-none bg-void border-line text-cream mt-2 h-11 focus-visible:ring-gold focus-visible:border-gold"
            data-testid="progress-value-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-smoke">Unit</label>
          <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
            <SelectTrigger className="rounded-none bg-void border-line text-cream mt-2 h-11" data-testid="progress-unit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface border-line text-cream">
              <SelectItem value="lbs">lbs</SelectItem>
              <SelectItem value="kg">kg</SelectItem>
              <SelectItem value="reps">reps</SelectItem>
              <SelectItem value="sec">sec</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="submit"
          disabled={submitting}
          className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase h-11 w-full sm:w-auto"
          data-testid="progress-submit-button"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </form>

      {exerciseNames.length > 0 && (
        <div className="bg-surface border border-line p-5 sm:p-6 mb-10">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="font-anton uppercase text-cream text-lg">Progression Chart</h2>
            <Select value={selectedExercise || ""} onValueChange={setSelectedExercise}>
              <SelectTrigger className="rounded-none bg-void border-line text-cream w-48" data-testid="progress-chart-exercise-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-surface border-line text-cream">
                {exerciseNames.map((n) => (
                  <SelectItem key={n} value={n}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="h-64 sm:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid stroke="#2a2722" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#A3A09A" fontSize={11} />
                <YAxis stroke="#A3A09A" fontSize={11} />
                <Tooltip contentStyle={{ background: "#13120E", border: "1px solid #2a2722", color: "#F5F5F0" }} />
                <Line type="monotone" dataKey="value" stroke="#9a8968" strokeWidth={2} dot={{ fill: "#9a8968" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {entries
          .slice()
          .reverse()
          .map((e) => (
            <div key={e.id} className="bg-surface border border-line p-4 flex items-center justify-between" data-testid={`progress-entry-${e.id}`}>
              <div>
                <p className="text-cream font-semibold">{e.exercise_name}</p>
                <p className="text-smoke text-sm">
                  {e.value} {e.unit} {e.reps ? `× ${e.reps} reps` : ""} · {e.date}
                </p>
              </div>
              <button onClick={() => handleDelete(e.id)} className="text-smoke hover:text-red-400" data-testid={`delete-progress-button-${e.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}
